import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtNum } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Receipt, Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const METRIC_LABELS = {
  reported_gwp: "Reported GWP",
  gross_com_exp_rpt: "GrossComExp-Rpt",
  gross_com_prov_rpt: "GrossComProv-Rpt",
  broker_comm_rpt: "BrokerComm-Rpt",
  broker_commission: "Broker Comm",
  policy_fees: "Policy Fees",
  premium_tax: "Premium Tax",
  bms_fee_rpt: "BMS Fee-Rpt",
  sl_tax: "SL Tax",
  sl_stamping_fee: "SL Stamping",
  sl_other_fees: "SL Other",
  mga_net_com_prov_rpt: "MGA Net Prov-Rpt",
  mga_net_com_exp_rpt: "MGA Net Exp-Rpt",
  ar_outstanding: "AR Outstanding",
};

const SEGMENTS = ["Youth & Amateur Sport", "Fitness", "Small Business & Programs", "Wholesale", "Collegiate/NIL"];
const SUBSIDIARIES = ["Covergame", "Players Health Cover", "PH I/C", "Other"];

function shortMonth(s) {
  if (!s) return "—";
  const t = String(s);
  const m = t.match(/^(\d{4})-(\d{2})/);
  if (m) {
    const date = new Date(`${m[1]}-${m[2]}-01T00:00:00Z`);
    return date.toLocaleString("en-US", { month: "short", year: "2-digit" });
  }
  return t.slice(0, 7);
}

export default function JournalEntries() {
  const [tab, setTab] = useState("rollup");

  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="je-page">
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center ring-1 ring-indigo-100">
            <Receipt className="w-5 h-5 text-indigo-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Journal entries</h2>
            <p className="text-sm text-slate-500 mt-1">
              Auto-rolled segment aggregates from production data, plus manual NetSuite-style entries you post directly.
            </p>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="rollup" data-testid="je-tab-rollup">Roll-ups (auto)</TabsTrigger>
          <TabsTrigger value="manual" data-testid="je-tab-manual">Manual entries</TabsTrigger>
        </TabsList>

        <TabsContent value="rollup" className="mt-4">
          <RollupView />
        </TabsContent>
        <TabsContent value="manual" className="mt-4">
          <ManualEntriesView />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RollupView() {
  const [segments, setSegments] = useState([]);
  const [segment, setSegment] = useState("__all__");
  const [data, setData] = useState({ rows: [], metrics: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/je/segments").then((r) => setSegments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = segment !== "__all__" ? { business_segment: segment } : {};
    api
      .get("/je/rollup", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [segment]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of data.rows) {
      const k = row.acct_month || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(row);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] > b[0] ? 1 : -1));
  }, [data]);

  const totals = useMemo(() => {
    const t = {};
    for (const m of data.metrics) {
      t[m] = data.rows.reduce((s, r) => s + (Number(r[m]) || 0), 0);
    }
    return t;
  }, [data]);

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-slate-600">
          Filter by segment:
        </div>
        <div className="w-[260px]">
          <Select value={segment} onValueChange={setSegment}>
            <SelectTrigger data-testid="je-segment-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All segments</SelectItem>
              {segments.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-[12px] text-slate-500">
          <span className="font-medium text-slate-900">{fmtNum(data.rows.length)}</span> rolled-up rows
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-medium sticky left-0 bg-white z-10">Acct Month</th>
                {segment === "__all__" && <th className="px-4 py-3 font-medium">Segment</th>}
                {data.metrics.map((m) => (
                  <th key={m} className="px-4 py-3 font-medium text-right whitespace-nowrap">
                    {METRIC_LABELS[m] || m}
                  </th>
                ))}
                <th className="px-4 py-3 font-medium text-right">#</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={data.metrics.length + 3} className="py-12 text-center text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading &&
                grouped.map(([month, rows]) =>
                  rows.map((r, i) => (
                    <tr key={`${month}-${r.segment ?? "x"}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2.5 font-medium text-slate-900 sticky left-0 bg-white">
                        {i === 0 ? shortMonth(month) : ""}
                      </td>
                      {segment === "__all__" && <td className="px-4 py-2.5 text-slate-600">{r.segment || "—"}</td>}
                      {data.metrics.map((m) => (
                        <td key={m} className="px-4 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">
                          {fmtMoney(r[m])}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{r.count}</td>
                    </tr>
                  ))
                )}
              {!loading && data.rows.length === 0 && (
                <tr>
                  <td colSpan={data.metrics.length + 3} className="py-12 text-center text-slate-400 text-sm">
                    No data.
                  </td>
                </tr>
              )}
            </tbody>
            {!loading && data.rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-medium">
                  <td className="px-4 py-3 sticky left-0 bg-slate-50">Totals</td>
                  {segment === "__all__" && <td />}
                  {data.metrics.map((m) => (
                    <td key={m} className="px-4 py-3 text-right tabular-nums text-slate-900 whitespace-nowrap">
                      {fmtMoney(totals[m])}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

function ManualEntriesView() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "controller";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState("__all__");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // entry being edited or "new" sentinel
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [totals, setTotals] = useState({ debit: 0, credit: 0, count: 0 });

  function refresh() {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (search.trim()) params.search = search.trim();
    if (segment !== "__all__") params.business_segment = segment;
    api
      .get("/journal-entries", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
    api.get("/journal-entries/totals").then((r) => setTotals(r.data)).catch(() => {});
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, segment]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const balanced = Math.abs((totals.debit || 0) - (totals.credit || 0)) < 0.01;

  return (
    <div className="space-y-4">
      {/* Toolbar + summary */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="je-search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Memo, entry #, account…"
                className="pl-9"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Segment</Label>
            <Select
              value={segment}
              onValueChange={(v) => {
                setSegment(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="mt-1 h-9" data-testid="je-segment-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {SEGMENTS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canWrite && (
            <Button
              onClick={() => setEditing({})}
              data-testid="je-new-button"
              className="h-9 bg-blue-600 hover:bg-blue-700 ml-auto"
            >
              <Plus className="w-4 h-4 mr-1" /> New entry
            </Button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Entries</div>
            <div className="text-lg font-semibold tabular-nums">{fmtNum(totals.count)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Total debit</div>
            <div className="text-lg font-semibold tabular-nums text-slate-900">{fmtMoney(totals.debit)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Total credit</div>
            <div className="text-lg font-semibold tabular-nums text-slate-900">{fmtMoney(totals.credit)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Balance</div>
            <div className={`text-lg font-semibold tabular-nums ${balanced ? "text-emerald-600" : "text-rose-600"}`}>
              {balanced ? "Balanced" : fmtMoney((totals.debit || 0) - (totals.credit || 0))}
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
          <div className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">{fmtNum(total)}</span> manual entries
          </div>
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid="je-page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>
              Page <b>{page}</b> / {totalPages}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="je-page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-medium">Entry #</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Posting</th>
                <th className="px-4 py-3 font-medium">Subsidiary</th>
                <th className="px-4 py-3 font-medium">Account</th>
                <th className="px-4 py-3 font-medium">Memo</th>
                <th className="px-4 py-3 font-medium">Segment</th>
                <th className="px-4 py-3 font-medium text-right">Debit</th>
                <th className="px-4 py-3 font-medium text-right">Credit</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-400 text-sm">Loading…</td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-12 text-center" data-testid="je-empty">
                    <Receipt className="w-8 h-8 mx-auto text-slate-300" strokeWidth={1.5} />
                    <div className="text-sm text-slate-500 mt-2">No manual entries yet.</div>
                    {canWrite && (
                      <div className="text-[12px] text-slate-400 mt-1">
                        Click <b>New entry</b> to post a NetSuite-style journal entry.
                      </div>
                    )}
                  </td>
                </tr>
              )}
              {items.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`je-row-${e.id}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40"
                >
                  <td className="px-4 py-2.5 font-medium text-slate-900">{e.entry_no || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{e.entry_date || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{e.posting_period || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{e.subsidiary || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600 max-w-[200px] truncate" title={e.ns_account}>
                    {e.ns_account || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 max-w-[260px] truncate" title={e.memo}>
                    {e.memo || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{e.business_segment || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{fmtMoney(e.debit)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{fmtMoney(e.credit)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {canWrite && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          data-testid={`je-edit-${e.id}`}
                          onClick={() => setEditing(e)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`je-delete-${e.id}`}
                          onClick={() => setConfirmDelete(e)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <JEEditDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this journal entry?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.entry_no}</b> • {confirmDelete?.memo}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="je-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="je-delete-confirm"
              onClick={async () => {
                try {
                  await api.delete(`/journal-entries/${confirmDelete.id}`);
                  toast.success("Entry deleted");
                  refresh();
                } catch (e) {
                  toast.error(e.response?.data?.detail || e.message);
                } finally {
                  setConfirmDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const JE_FIELDS = [
  { key: "entry_no", label: "Entry #", required: true },
  { key: "entry_date", label: "Date", type: "date", required: true },
  { key: "posting_period", label: "Posting period", placeholder: "e.g. Apr 2026" },
  { key: "subsidiary", label: "Subsidiary", select: SUBSIDIARIES },
  { key: "currency", label: "Currency", placeholder: "USD" },
  { key: "ns_account", label: "NS account", placeholder: "405001 Written Premium" },
  { key: "qbo_account", label: "QBO account" },
  { key: "memo", label: "Memo", textarea: true },
  { key: "debit", label: "Debit", type: "number" },
  { key: "credit", label: "Credit", type: "number" },
  { key: "business_segment", label: "Business segment", select: SEGMENTS },
  { key: "department", label: "Department" },
  { key: "policy_status", label: "Policy status", select: ["New", "Renew", "Cancel", "Endorsement"] },
];

function JEEditDialog({ entry, onClose, onSaved }) {
  const isOpen = entry !== null;
  const isCreate = isOpen && !entry?.id;
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isCreate) {
      setDraft({
        entry_no: "",
        entry_date: new Date().toISOString().slice(0, 10),
        posting_period: "",
        subsidiary: "Covergame",
        currency: "USD",
        ns_account: "",
        qbo_account: "",
        memo: "",
        debit: 0,
        credit: 0,
        business_segment: "",
        department: "US MGA",
        policy_status: "New",
      });
    } else {
      setDraft({ ...entry });
    }
  }, [entry, isOpen, isCreate]);

  async function save() {
    if (!draft.entry_no?.trim() || !draft.entry_date) {
      toast.error("Entry # and date are required");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/journal-entries", draft);
        toast.success("Entry created");
      } else {
        await api.put(`/journal-entries/${entry.id}`, draft);
        toast.success("Entry updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="je-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isCreate ? "New journal entry" : `Edit ${draft.entry_no || "entry"}`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            NetSuite-style line item. Use debit OR credit (one will typically be zero).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {JE_FIELDS.map((f) => (
            <div
              key={f.key}
              className={`space-y-1 ${f.textarea ? "sm:col-span-2" : ""}`}
            >
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </Label>
              {f.select ? (
                <Select
                  value={draft[f.key] || ""}
                  onValueChange={(v) => setDraft({ ...draft, [f.key]: v })}
                >
                  <SelectTrigger data-testid={`je-field-${f.key}`} className="h-9">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {f.select.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.textarea ? (
                <textarea
                  data-testid={`je-field-${f.key}`}
                  value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={f.placeholder}
                />
              ) : (
                <Input
                  data-testid={`je-field-${f.key}`}
                  type={f.type || "text"}
                  step={f.type === "number" ? "0.01" : undefined}
                  value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} data-testid="je-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="je-save"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? "Saving…" : isCreate ? "Create entry" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
